import { debounce, has, isBoolean, isEmpty, isEqual } from 'lodash'
import React from 'react'
import Api from '../api'
import { useDebug, useEventBus } from '../hooks'
import useGTM from '../hooks/useGTM'
import { Helpers } from '../utils'
import { transformCartItemData } from '../utils/helpers'

const initialState: any = {
	// config, translations, data 
	items: {},
	notices: {},
	itemsCount: 0,
	itemsSubtotal: 0,
	discountAmount: 0,

	updating: false,
	opened: false,
}

// removeCartItem: (cartItemID: string) => {},
// updateCartItem: (cartItemID: string, newQuantity: number) => {},
// cartItems: [], 
// cartItemsSubtotal: 0, 
// cartItemsCount: 0,

// isMinicartOpened: false,

// translations: {},
// links: {},

// TODO: Base Product provider and child providers for diff product types
// SingleProductContext
// VariableProductContext
// BundledProductContext

export const MinicartContext = React.createContext<any>({})

const MinicartProvider: React.FC<any> = ({ children, loading, config, theme, translations, data }) => {
	const [store, updateStore] = React.useState<any>({ ...initialState });

	const eventBus = useEventBus();

	const gtm = useGTM(config.general);

	const setState = React.useCallback((key: string, value: any) => {
		if (!(key in initialState)) throw Error(`Key ${key} is not valid state.`);
		updateStore((prevStore: any) => ({ ...prevStore, [key]: value instanceof Function ? value(prevStore[key]) : value, }))
	}, [])

	// Toggles any boolean state
	const toggleState = (key: string, force?: boolean) => {

		if (!(key in initialState)) throw Error(`Key ${key} is not a valid state.`);
		if (typeof initialState[key] !== 'boolean') throw Error(`Key ${key} is not a valid bool state.`);

		setState(key, (prevState: any) => isBoolean(force) ? force : !prevState);

	}

	const handleMinicartOpen = () => {
		if (config.general.minicartEnabled) {
			setState('opened', true);
		} else {
			if (config.links.cart.url !== window.location.href) {
				window.open(config.links.cart.url, '_self');
			}
		}
	}

	React.useEffect(() => {
		if (loading || isEmpty(data?.items)) return;

		// if ( isEqual(data?.items, store.items) ) return;

		setState('discountAmount', data?.discountAmount ?? 0);

		setState('items', Object.fromEntries(Object.entries(data?.items).map(([cartItemID, item]: any) => {

			const newItem = { ...item };

			if (isEmpty(item?.bundledBy)) {
				newItem.price = item?.price?.sale || item.price?.regular || 0;
				const priceArgs = {
					displayMode: isBoolean(item.taxable) ? (item.taxable ? 'include' : 'exclude') : null ?? config.general.tax.displayMode,
					taxClass: !isEmpty(item?.taxClass) ? item.taxClass : config.general.tax.default,
					taxRates: config.general.tax.rates,
					precision: Math.abs(config.general.roundingPrecision),
					price: newItem.price
				}

				newItem.pricePerUnit = Helpers.getPrice({ ...priceArgs, quantity: 1 });
				newItem.subtotal = Helpers.getPrice({ ...priceArgs, quantity: newItem.quantity.selected });
			} else {
				newItem.subtotal = 0;
			}

			return [cartItemID, newItem];

		})))
	}, [loading, data])

	// const getCartItemsCount = (items: any) => Object.keys(items).length
	const getItemsSubtotal = React.useCallback(() => Object.values(store.items).reduce((subtotal: any, item: any) => subtotal + (isEmpty(item?.bundledBy) ? item.subtotal : 0), 0), [store]);

	const itemsCount = React.useMemo(() => Object.values(store.items).reduce((quantity: any, item: any) => quantity + ((isEmpty(item?.bundledBy) && ! has(item.meta, 'wa_cart_gift')) ? item.quantity.selected : 0), 0), [store]);
	const itemsSubtotal = React.useMemo(() => {
		const subtotalValue: any = getItemsSubtotal();
		const discountValue: any = store.discountAmount ? Math.abs(store.discountAmount) : 0;

		if (discountValue <= 0) {
			return subtotalValue;
		}

		const newSubtotalValue = subtotalValue - discountValue;

		return newSubtotalValue > 0 ? newSubtotalValue : 0;
	}, [store]);

	// const cartItemsCount = React.useMemo(() => {

	// }, []);

	// Always override the subtotal state when it gets updated from API
	// NOTE: Expecting it to be in provided in expected tax class 

	// Helpers
	const debouncedHandleUpdateCartItem = async ({ name, value, cartItemID }: { name: string, value: any, cartItemID: string }) => {

		console.log('debounced', store.items);
		//if (name === 'quantity' && isEqual(store.items[cartItemID][name].selected, value)) return;

		setState('updating', true);
		const response: any = await Api.updateCartItem(cartItemID, { [name]: value });

		if (isEmpty(response?.errors)) {
			eventBus.dispatch('Notices.updated', response?.notices);
		} else {
			eventBus.dispatch('Notices.updated', response?.errors?.response?.data?.notices);
		}

		//const { items, subtotal } = response.data.data;

		if (response.data && 'items' in response?.data) {
			//setState('items', response.data.items);

			if (name === 'quantity') {
				const updatedProductItem = response.data.items[cartItemID] ?? null;
				console.log({ updatedCartItemID: cartItemID, updatedProductItem })

				if (updatedProductItem) {
					const updateQtyDiff = Number(updatedProductItem?.quantity.selected) - Number(updatedProductItem?.quantity?.previous);
					if (updateQtyDiff > 0) {
						gtm.addProductToCart(transformCartItemData(updatedProductItem), Math.abs(updateQtyDiff));
					} else {
						gtm.removeProductFromCart(transformCartItemData(updatedProductItem), Math.abs(updateQtyDiff));
					}
					eventBus.dispatch('Minicart.updatedCart', { action: 'update', productID: response.data.items[cartItemID].id, id: cartItemID, data: response.data, quantity: { diff: updateQtyDiff, previous: updatedProductItem.quantity.previous, selected: updatedProductItem.quantity.selected } });
				}
			}

			setState('notices', response.data.notices);
			setState('discountAmount', response.data.discountAmount);
		}

		setState('updating', false);
		//return response;

	};


	const ajaxHandleUpdateCartItem = React.useMemo(
		() => debounce(debouncedHandleUpdateCartItem, 300)
		, []);


	const handleItemUpdate = ({ name, value, cartItemID }: { name: string, value: any, cartItemID: string }) => {

		if (loading || store.updating) return;
		if (!(cartItemID in store.items)) throw Error(`Update: Cart item #${cartItemID} is not longer contained in cart.`);
		if (!(name in store.items[cartItemID])) throw Error(`Update: Cart item #${cartItemID} does not contain field ${name}.`);
		if (!isEmpty(store.items[cartItemID]?.bundledBy)) return;
		if (name === 'quantity' && isEqual(store.items[cartItemID][name].selected, value)) {
			ajaxHandleUpdateCartItem.cancel();
			return;
		};

		setState('items', (prevState: any) => {

			const newState = { ...prevState };

			if (name === 'quantity') {

				if (isEmpty(prevState[cartItemID]?.bundledBy)) {
					newState[cartItemID][name].selected = value;

					const priceArgs = {
						displayMode: prevState[cartItemID].displayMode ?? config.general.tax.displayMode,
						taxClass: !isEmpty(prevState[cartItemID]?.taxClass) ? prevState[cartItemID].taxClass : config.general.tax.default,
						taxRates: config.general.tax.rates,
						precision: Math.abs(config.general.roundingPrecision),
						quantity: prevState[cartItemID].quantity.selected,
						price: prevState[cartItemID].price
					}

					newState[cartItemID]['subtotal'] = Helpers.getPrice(priceArgs) ?? 0;
				} else {
					newState[cartItemID]['subtotal'] = 0;
				}

			}

			return newState;
		})

		ajaxHandleUpdateCartItem({ name, value, cartItemID });

	}

	const handleItemRemove = ({ cartItemID }: { cartItemID: string }) => {

		if (loading || store.updating) return;
		if (!(cartItemID in store.items)) throw Error(`Remove: Cart item #${cartItemID} is not longer contained in cart.`);
		if (!isEmpty(store.items[cartItemID]?.bundledBy)) return;

		let productID = '';

		setState('items', (prevState: any) => {
			const newState: any = { ...prevState };
			productID = newState[cartItemID].id;
			delete newState[cartItemID];
			if (!isEmpty(prevState[cartItemID]['bundledItems'])) {
				prevState[cartItemID]['bundledItems'].forEach((itemCartID: string) => delete newState[itemCartID])
			}
			return newState;
		})

		debouncedRemoveItem({ cartItemID, productID });
	}

	// Helpers
	const debouncedRemoveItem = async ({ cartItemID, productID }: { productID?: string, cartItemID: string }) => {

		setState('updating', true);
		const response: any = await Api.removeCartItem(cartItemID);

		if (isEmpty(response?.errors)) {
			eventBus.dispatch('Notices.updated', response?.notices);
		} else {
			eventBus.dispatch('Notices.updated', response?.errors?.response?.data?.notices);
		}

		//const { items, subtotal } = response.data.data;

		const removedCartItem = store.items[cartItemID] ?? null;
		if (removedCartItem) {
			console.log({
				removedItemPrice: removedCartItem.pricePerUnit,
				removedItemQuantity: removedCartItem.quantity.selected,
			});

			gtm.removeProductFromCart(transformCartItemData({ ...removedCartItem, price: { current: removedCartItem.pricePerUnit } }), removedCartItem.quantity.selected);
		}

		if (response.data && 'items' in response?.data) {
			setState('items', response.data.items);
			eventBus.dispatch('Minicart.updatedCart', { action: 'delete', productID, id: cartItemID, data: response.data, updatedCartItem: removedCartItem, quantity: { diff: -1 * removedCartItem?.quantity?.selected, previous: removedCartItem?.quantity?.selected, selected: 0 } });
			setState('notices', response.data.notices);
			setState('discountAmount', response.data.discountAmount);
		}

		setState('updating', false);
		//return response;

	};

	React.useEffect(() => {

		eventBus.on('toggleMinicartSidebar', (force: boolean | null = null) => {
			setTimeout(() => {
				requestAnimationFrame(() => {
					setState('opened', (prevState: boolean) => force ?? !prevState);
				})
			}, 50)
		});

		return () => eventBus.remove('toggleMinicartSidebar');

	}, []);

	return (
		<MinicartContext.Provider
			value={{
				...store,
				itemsCount,
				itemsSubtotal,
				realSubtotal: getItemsSubtotal(),
				config: { ...config, notices: { ...config.notices, ...(store.notices ?? {}) } },
				theme,
				translations,
				loading,
				setState,
				toggleState,
				handleItemUpdate,
				handleItemRemove,
				handleMinicartOpen
			}}
			children={!isEmpty(data) ? children : null}
		/>
	)
}

export default MinicartProvider;